import { useEffect, useState, useCallback } from 'react';
import { ArrowRight, X } from 'lucide-react';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface ToastAction {
  /** Fired when the user clicks the toast body. Toast auto-dismisses afterward. */
  onClick: () => void;
  /** Optional aria-label / tooltip — defaults to "Open" if omitted. */
  label?: string;
}

export interface ToastData {
  id: number;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

export interface ToastOptions {
  action?: ToastAction;
}

// --- Imperative API ---

type ToastListener = (toast: ToastData) => void;
const listeners = new Set<ToastListener>();
let nextId = 1;

export function showToast(
  message: string,
  type: ToastType = 'info',
  options?: ToastOptions,
): void {
  const toast: ToastData = { id: nextId++, message, type, action: options?.action };
  for (const listener of listeners) {
    listener(toast);
  }
}

function subscribeToToasts(listener: ToastListener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

// --- Components ---

function Toast({ toast, onDismiss }: { toast: ToastData; onDismiss: (id: number) => void }) {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), 5000);
    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);

  const alertClass = {
    info: 'alert-info',
    success: 'alert-success',
    warning: 'alert-warning',
    error: 'alert-error',
  }[toast.type];

  const actionable = !!toast.action;
  const handleAction = () => {
    if (!toast.action) return;
    try {
      toast.action.onClick();
    } finally {
      onDismiss(toast.id);
    }
  };

  return (
    <div
      className={`alert ${alertClass} shadow-lg py-2 px-4 flex items-center gap-2 text-sm animate-slide-in ${
        actionable ? 'cursor-pointer hover:brightness-110' : ''
      }`}
      onClick={actionable ? handleAction : undefined}
      role={actionable ? 'button' : undefined}
      title={actionable ? toast.action?.label ?? 'Open' : undefined}
    >
      <span className="flex-1">{toast.message}</span>
      {actionable && <ArrowRight className="w-3.5 h-3.5 flex-shrink-0 opacity-70" />}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(toast.id);
        }}
        className="btn btn-ghost btn-xs btn-circle"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const handleDismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    return subscribeToToasts((toast) => {
      setToasts((prev) => [...prev.slice(-4), toast]); // Keep max 5 toasts
    });
  }, []);

  if (toasts.length === 0) return null;

  return (
    // Bottom-CENTER, not bottom-right: an OSR panel (Chat/Browser) docked to a
    // side edge is a full-height strip that composites OVER everything in its
    // column, hiding right-corner toasts. Center sits over the DOM editor panels
    // (terminal/files), which don't composite-over.
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] flex flex-col items-center gap-2 max-w-sm">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={handleDismiss} />
      ))}
    </div>
  );
}
